import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalAdminLayout from './PortalAdminLayout';
import './PortalDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';

const FraudDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [flaggedCustomers, setFlaggedCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [sortBy, setSortBy] = useState('-fraudMetrics.fraudScore');
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [showClearModal, setShowClearModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [clearReason, setClearReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(30);
  const [suspendReason, setSuspendReason] = useState('');

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      navigate('/login');
      return false;
    }

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === 'portal_admin' ||
        decoded.role === 'R6' ||
        (decoded.roles && (decoded.roles.includes('portal_admin') || decoded.roles.includes('R6')));

      if (!hasPortalAdminAccess) {
        navigate('/portal-admin');
        return false;
      }
      return true;
    } catch (error) {
      navigate('/login');
      return false;
    }
  }, [navigate]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await fetch(`${API_BASE_URL}/api/fraud/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      }
    } catch (error) {
    }
  }, []);

  const fetchFlaggedCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ogestyle_token');
      const response = await fetch(
        `${API_BASE_URL}/api/fraud/flagged-customers?sort=${sortBy}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setFlaggedCustomers(data.data);
      } else if (response.status === 403) {
        navigate('/portal-admin');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [sortBy, navigate]);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchStatistics();
      fetchFlaggedCustomers();
    }
  }, [checkPortalAdmin, fetchStatistics, fetchFlaggedCustomers, sortBy]);

  const fetchCustomerDetails = async (customerId) => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await fetch(`${API_BASE_URL}/api/fraud/customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomerDetails(data.data);
      }
    } catch (error) {
    }
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetails(customer._id);
  };

  const handleClearFlag = async () => {
    if (!selectedCustomer) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem('ogestyle_token');
      const response = await fetch(
        `${API_BASE_URL}/api/fraud/clear-flag/${selectedCustomer._id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: clearReason })
        }
      );

      if (response.ok) {
        alert('Fraud flag cleared successfully');
        setShowClearModal(false);
        setClearReason('');
        fetchFlaggedCustomers();
        fetchStatistics();
        setSelectedCustomer(null);
        setCustomerDetails(null);
      }
    } catch (error) {
      alert('Failed to clear fraud flag');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendSuspension = async () => {
    if (!selectedCustomer) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem('ogestyle_token');
      const response = await fetch(
        `${API_BASE_URL}/api/fraud/extend-suspension/${selectedCustomer._id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            days: parseInt(suspendDays),
            reason: suspendReason
          })
        }
      );

      if (response.ok) {
        alert('Suspension extended successfully');
        setShowSuspendModal(false);
        setSuspendDays(30);
        setSuspendReason('');
        fetchFlaggedCustomers();
        fetchCustomerDetails(selectedCustomer._id);
      }
    } catch (error) {
      alert('Failed to extend suspension');
    } finally {
      setActionLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Fraud Score', 'Status', 'Failed Bookings', 'Cancellations', 'Refunds'];
    const rows = flaggedCustomers.map((customer) => [
      `${customer.firstName} ${customer.lastName}`,
      customer.email,
      customer.fraudMetrics.fraudScore,
      customer.isSuspended ? 'Suspended' : 'Active',
      customer.fraudMetrics.failedBookingReAttemptCount30Days || 0,
      customer.fraudMetrics.cancellationCount30Days || 0,
      customer.fraudMetrics.refundRequestCount30Days || 0
    ]);

    const csv =
      headers.join(',') +
      '\n' +
      rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flagged-customers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getRiskColor = (score) => {
    if (score >= 70) return '#dc3545';
    if (score >= 40) return '#fd7e14';
    return '#ffc107';
  };

  const getRiskLabel = (score) => {
    if (score >= 70) return 'High Risk';
    if (score >= 40) return 'Medium Risk';
    return 'Low Risk';
  };

  if (loading && !statistics) {
    return (
      <PortalAdminLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading fraud detection data...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
            🛡️ Fraud Detection
          </h1>
          <button
            onClick={exportToCSV}
            style={{
              padding: '10px 20px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            📊 Export CSV
          </button>
        </div>

        {/* Statistics Overview */}
        {statistics && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                Total Flagged
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc3545' }}>
                {statistics.totalFlagged}
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                High Risk
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc3545' }}>
                {statistics.highRisk}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                Score ≥ 70
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                Medium Risk
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#fd7e14' }}>
                {statistics.mediumRisk}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                Score 40-69
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                Suspended
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#6366f1' }}>
                {statistics.suspended}
              </div>
            </div>
          </div>
        )}

        {/* Sort Controls */}
        <div style={{
          background: 'white',
          padding: '15px 20px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          <label style={{ fontSize: '14px', fontWeight: '600', marginRight: '10px', color: '#374151' }}>
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="-fraudMetrics.fraudScore">Fraud Score (High to Low)</option>
            <option value="fraudMetrics.fraudScore">Fraud Score (Low to High)</option>
            <option value="-createdAt">Recently Added</option>
            <option value="lastName">Last Name (A-Z)</option>
          </select>
        </div>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedCustomer ? '1fr 1fr' : '1fr',
          gap: '20px',
          minHeight: '500px'
        }}>
          {/* Flagged Customers List */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: '#111827' }}>
                Flagged Customers ({flaggedCustomers.length})
              </h2>
            </div>
            
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {flaggedCustomers.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
                  <p style={{ fontSize: '14px' }}>No flagged customers found</p>
                </div>
              ) : (
                flaggedCustomers.map((customer) => (
                  <div
                    key={customer._id}
                    onClick={() => handleCustomerClick(customer)}
                    style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      background: selectedCustomer?._id === customer._id ? '#eff6ff' : 'white',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCustomer?._id !== customer._id) {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCustomer?._id !== customer._id) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                          {customer.firstName} {customer.lastName}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {customer.email}
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: getRiskColor(customer.fraudMetrics.fraudScore) + '20',
                        color: getRiskColor(customer.fraudMetrics.fraudScore)
                      }}>
                        {customer.fraudMetrics.fraudScore}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: '#6b7280' }}>
                      {customer.fraudMetrics.failedBookingReAttemptCount30Days > 0 && (
                        <span>❌ {customer.fraudMetrics.failedBookingReAttemptCount30Days} failed</span>
                      )}
                      {customer.fraudMetrics.cancellationCount30Days > 0 && (
                        <span>🚫 {customer.fraudMetrics.cancellationCount30Days} cancelled</span>
                      )}
                      {customer.fraudMetrics.refundRequestCount30Days > 0 && (
                        <span>💸 {customer.fraudMetrics.refundRequestCount30Days} refunds</span>
                      )}
                    </div>

                    {customer.isSuspended && (
                      <div style={{
                        marginTop: '8px',
                        padding: '6px 10px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        🔒 SUSPENDED
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Customer Details Panel */}
          {selectedCustomer && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h2 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0', color: '#111827' }}>
                      {selectedCustomer.firstName} {selectedCustomer.lastName}
                    </h2>
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                      {selectedCustomer.email}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedCustomer(null);
                      setCustomerDetails(null);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ padding: '20px', maxHeight: '600px', overflowY: 'auto' }}>
                {/* Risk Assessment */}
                <div style={{
                  padding: '16px',
                  background: getRiskColor(selectedCustomer.fraudMetrics.fraudScore) + '15',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: `2px solid ${getRiskColor(selectedCustomer.fraudMetrics.fraudScore)}50`
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                    Fraud Score
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: getRiskColor(selectedCustomer.fraudMetrics.fraudScore) }}>
                    {selectedCustomer.fraudMetrics.fraudScore}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: getRiskColor(selectedCustomer.fraudMetrics.fraudScore), marginTop: '4px' }}>
                    {getRiskLabel(selectedCustomer.fraudMetrics.fraudScore)}
                  </div>
                </div>

                {/* Fraud Metrics */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
                    Fraud Indicators (Last 30 Days)
                  </h3>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f9fafb', borderRadius: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>Failed Booking Attempts</span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#dc3545' }}>
                        {selectedCustomer.fraudMetrics.failedBookingReAttemptCount30Days || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f9fafb', borderRadius: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>Cancellations</span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#fd7e14' }}>
                        {selectedCustomer.fraudMetrics.cancellationCount30Days || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f9fafb', borderRadius: '6px' }}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>Refund Requests</span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#ffc107' }}>
                        {selectedCustomer.fraudMetrics.refundRequestCount30Days || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowClearModal(true)}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '10px 16px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Flag
                  </button>
                  <button
                    onClick={() => setShowSuspendModal(true)}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '10px 16px',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Extend Suspension
                  </button>
                </div>

                {/* Recent Bookings */}
                {customerDetails?.bookings && customerDetails.bookings.length > 0 && (
                  <div style={{ marginTop: '30px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
                      Recent Bookings
                    </h3>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {customerDetails.bookings.map((booking) => (
                        <div
                          key={booking._id}
                          style={{
                            padding: '12px',
                            background: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                              {booking.serviceName}
                            </span>
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                background:
                                  booking.status === 'completed'
                                    ? '#d1fae5'
                                    : booking.status === 'cancelled'
                                    ? '#fee2e2'
                                    : '#fef3c7',
                                color:
                                  booking.status === 'completed'
                                    ? '#065f46'
                                    : booking.status === 'cancelled'
                                    ? '#991b1b'
                                    : '#92400e'
                              }}
                            >
                              {booking.status}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {new Date(booking.appointmentDate).toLocaleDateString()} • $
                            {booking.totalPrice?.toFixed(2)}
                          </div>
                          {booking.refundStatus === 'refunded' && (
                            <div style={{
                              marginTop: '6px',
                              fontSize: '12px',
                              color: '#dc3545',
                              fontWeight: '600'
                            }}>
                              💸 Refunded ${booking.refundAmount?.toFixed(2)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Clear Flag Modal */}
        {showClearModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              zIndex: 1000
            }}
            onClick={() => setShowClearModal(false)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '500px',
                width: '100%'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
                Clear Fraud Flag
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                Are you sure you want to clear the fraud flag for {selectedCustomer.firstName}{' '}
                {selectedCustomer.lastName}?
              </p>
              <textarea
                placeholder="Reason for clearing flag (optional)"
                value={clearReason}
                onChange={(e) => setClearReason(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '16px',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowClearModal(false);
                    setClearReason('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearFlag}
                  disabled={actionLoading}
                  style={{
                    padding: '10px 20px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.6 : 1
                  }}
                >
                  {actionLoading ? 'Clearing...' : 'Clear Flag'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Extend Suspension Modal */}
        {showSuspendModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              zIndex: 1000
            }}
            onClick={() => setShowSuspendModal(false)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '500px',
                width: '100%'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
                Extend Suspension
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                Extend suspension for {selectedCustomer.firstName} {selectedCustomer.lastName}
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>
                  Number of days:
                </label>
                <input
                  type="number"
                  min="1"
                  value={suspendDays}
                  onChange={(e) => setSuspendDays(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>
                  Reason:
                </label>
                <textarea
                  placeholder="Reason for suspension extension"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowSuspendModal(false);
                    setSuspendDays(30);
                    setSuspendReason('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendSuspension}
                  disabled={actionLoading}
                  style={{
                    padding: '10px 20px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.6 : 1
                  }}
                >
                  {actionLoading ? 'Extending...' : 'Extend Suspension'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default FraudDashboard;
